import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import './CreditPurchaseModal.css';

const CreditPurchaseModal = ({ 
  isOpen, 
  onClose, 
  currentUsage, 
  onPurchaseSuccess 
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(500);

  const creditOptions = [
    {
      quantity: 500,
      price: 2,
      recommended: false,
      descriptionKey: 'creditPurchase.option.basic',
      savingsKey: null
    },
    {
      quantity: 1000,
      price: 3,
      recommended: true,
      descriptionKey: 'creditPurchase.option.popular',
      savingsKey: 'creditPurchase.option.savings.1000'
    },
    {
      quantity: 2500,
      price: 7,
      recommended: false,
      descriptionKey: 'creditPurchase.option.premium',
      savingsKey: 'creditPurchase.option.savings.2500'
    }
  ];

  const handlePurchase = async () => {
    setLoading(true);
    
    try {
      // Créer la session de paiement
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usage/purchase-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          quantity: selectedOption,
          type: 'api_calls'
        })
      });
      
      if (response.ok) {
        const { checkoutUrl, sessionId } = await response.json();
        
        // Pour la démo, on simule un paiement réussi
        // Dans la vraie version, l'utilisateur serait redirigé vers Stripe
        const option = creditOptions.find(opt => opt.quantity === selectedOption);
        const confirmPurchase = window.confirm(
          t('creditPurchase.confirmPurchase', { price: option?.price, quantity: selectedOption.toLocaleString() })
        );
        
        if (confirmPurchase) {
          // Simuler la completion du paiement
          const completionResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/usage/complete-purchase`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ 
              sessionId,
              success: true
            })
          });
          
          if (completionResponse.ok) {
            const result = await completionResponse.json();
            toast.success(t('creditPurchase.purchaseSuccess', { message: result.message }));

            if (onPurchaseSuccess) {
              onPurchaseSuccess(selectedOption);
            }

            onClose();
          } else {
            const error = await completionResponse.json();
            toast.error(t('creditPurchase.purchaseError', { error: error.error }));
          }
        }
        
      } else {
        const error = await response.json();
        toast.error(t('creditPurchase.purchaseError', { error: error.error }));
      }
    } catch (error) {
      console.error('❌', t('creditPurchase.errorLog'), error);
      toast.error(t('creditPurchase.purchaseException', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="credit-purchase-overlay">
      <div className="credit-purchase-modal">
        <div className="credit-purchase-header">
          <h3>{t('creditPurchase.title')}</h3>
          <button 
            className="credit-purchase-close"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className="credit-purchase-content">
          <div className="current-usage-info">
            <div className="usage-stat">
              <span className="usage-label">{t('creditPurchase.currentUsageLabel')}</span>
              <span className="usage-value">
                {currentUsage?.used?.toLocaleString() || 0} / {currentUsage?.limit?.toLocaleString() || 0}
              </span>
            </div>
            <div className="usage-remaining">
              {currentUsage?.remaining?.toLocaleString() || 0} {t('creditPurchase.unit.apiCallsRemaining')}
            </div>
          </div>

          <div className="credit-options">
            <h4>{t('creditPurchase.choosePack')}</h4>
            <div className="credit-options-grid">
              {creditOptions.map((option) => (
                <div 
                  key={option.quantity}
                  className={`credit-option ${selectedOption === option.quantity ? 'selected' : ''} ${option.recommended ? 'recommended' : ''}`}
                  onClick={() => setSelectedOption(option.quantity)}
                >
                  {option.recommended && (
                    <div className="recommended-badge">{t('creditPurchase.popularBadge')}</div>
                  )}
                  
                  <div className="credit-option-header">
                    <span className="credit-quantity">
                      {option.quantity.toLocaleString()}
                    </span>
                    <span className="credit-unit">{t('creditPurchase.unit.apiCalls')}</span>
                  </div>
                  
                  <div className="credit-price">
                    {option.price}€
                  </div>
                  
                  <div className="credit-description">
                    {t(option.descriptionKey)}
                  </div>
                  
                  {option.savingsKey && (
                    <div className="credit-savings">
                      {t(option.savingsKey)}
                    </div>
                  )}
                  
                  <div className="credit-per-call">
                    {(option.price / option.quantity * 1000).toFixed(2)}€ / {t('creditPurchase.unit.per1000')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="purchase-summary">
            <div className="summary-row">
              <span>{t('creditPurchase.summary.selectedQuantity')}</span>
              <span>{selectedOption.toLocaleString()} {t('creditPurchase.unit.apiCalls')}</span>
            </div>
            <div className="summary-row">
              <span>{t('creditPurchase.summary.price')}</span>
              <span>{creditOptions.find(opt => opt.quantity === selectedOption)?.price}€</span>
            </div>
            <div className="summary-row total">
              <span>{t('creditPurchase.summary.newLimit')}</span>
              <span>{((currentUsage?.limit || 0) + selectedOption).toLocaleString()} {t('creditPurchase.unit.apiCalls')}</span>
            </div>
          </div>
        </div>

        <div className="credit-purchase-footer">
          <button 
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button 
            className="btn-primary"
            onClick={handlePurchase}
            disabled={loading}
          >
            {loading ? `${t('creditPurchase.processing')}` : t('creditPurchase.buyButton', { quantity: selectedOption.toLocaleString() })}
          </button>
        </div>

        <div className="credit-purchase-info">
          <small>
            💡 {t('creditPurchase.info')}
          </small>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchaseModal;
